<script lang="ts">
  import { onDestroy, onMount } from "svelte";
  import Router, { replace, location } from "svelte-spa-router";
  import Menu from "./components/Menu/Menu.svelte";
  import { dialogTypes, serverlessRoutes } from "./lib/constants";
  import { getWithJwt } from "./lib/requests";
  import { user } from "./stores/userStore";
  import { theme } from "./stores/themeStore";
  import { routes } from "./lib/routes";
  import Dialog from "./components/Dialogs/Dialog.svelte";
  import { dialogData } from "./stores/dialogDataStore";
  import { useSocket } from "./lib/hooks/useSocket";
  import Overlay from "./components/UI/Overlay.svelte";
  import Alert from "./components/UI/Alert.svelte";
  import { showAlert } from "./stores/showAlertStore";
  import { Capacitor, SystemBars, SystemBarsStyle } from "@capacitor/core";
  import { Keyboard } from "@capacitor/keyboard";
  import { App } from "@capacitor/app";
  import { fetchUser } from "./lib/fetchUser";
  import { fetchChatSessions } from "./lib/fetchSessions";
  import { chatSessions, chatSessionsCount, showOnlyUnreadChat } from "./stores/chatSessionsStore";
  import { isClient } from "./lib/roles";
  import { alertStore, selectedIds } from "stores";
  import { flip } from "svelte/animate";
  import { storage } from "lib";
  import OnlineIndicator from "./components/OnlineIndicator.svelte";
  import JobSpinner from "./components/JobSpinner.svelte";

  let interval;
  let showOnlineIndicator = false;
  const closeDialog = () => {
    selectedIds.set([]);
    dialogData.update((current) => ({
      ...current,
      type: "",
      data: {},
      isCloseable: true,
      component: undefined,
    }));
  };

  const setTheme = async () => {
    $theme = await storage.get("theme");

    if ($theme === "dark") {
      document.documentElement.classList.add("dark");
      if (Capacitor.isNativePlatform()) await SystemBars.setStyle({ style: SystemBarsStyle.Dark });
    } else {
      document.documentElement.classList.remove("dark");
      if (Capacitor.isNativePlatform()) await SystemBars.setStyle({ style: SystemBarsStyle.Light });
    }
  };

  let unsubscribeLocation = location.subscribe((): void => {
    window.scrollTo({ top: 0 });
  });

  const checkAppVersion = async (): Promise<void> => {
    if (!Capacitor.isNativePlatform()) { return; }

    const [response, appInfo, ignoreUpdate] = await Promise.all([
      getWithJwt(`${serverlessRoutes.PATCH_VERSION}/latest`),
      App.getInfo(),
      storage.get("ignoreUpdate")
    ]);

    const {latestVersion} = response.data;
    const currentVersion = appInfo.version;
    let shouldPrompt = false;

    if (parseFloat(latestVersion) > parseFloat(currentVersion)) {
      if (!ignoreUpdate) {
        shouldPrompt = true;
      } else {
        const ignoreUpdateDate = new Date(ignoreUpdate);
        const threeDaysAgo = new Date();

        threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);

        if (ignoreUpdateDate < threeDaysAgo) { shouldPrompt = true; }
      }
    }

    if (!shouldPrompt) { return; }

    $dialogData.data = {currentVersion, latestVersion};
    $dialogData.type = dialogTypes.FORCE_UPDATE;
  };

  onMount(async () => {
    setTimeout(async () => {
      if (isClient($user)) {
        if ($user.id !== 216072) {
          // cypress
          let dontShowSetDateOfBirth = await storage.get("dontShowSetDateOfBirth");

          const response = await getWithJwt(serverlessRoutes.USER_DATA);

          if (!response.data.user?.dateOfBirth) {
            if (
              !dontShowSetDateOfBirth ||
              (dontShowSetDateOfBirth && dontShowSetDateOfBirth === false)
            ) {
              $dialogData.data = {};
              $dialogData.type = dialogTypes.SET_DATE_OF_BIRTH;
            }
          }
        }
      }
    }, 1000);

    if (Capacitor.isNativePlatform()) {
      App.addListener("appStateChange", async ({ isActive }) => {
        if (isActive) {
          await fetchUser();
          await fetchChatSessions();
        }
      });
    }
    App.addListener("backButton", ({ canGoBack }) => {
      const canCloseDialog = $dialogData.isCloseable != null ? $dialogData.isCloseable : true;

      if ($dialogData.type && canCloseDialog) {
        closeDialog();
        return;
      }

      if (!canGoBack) {
        App.exitApp();
      } else {
        window.history.back();
      }
    });
    if ($location === "/" && $user) {
      if (isClient($user)) await replace("/training/program");
      else await replace("/clients/list");
    }

    const root = document.documentElement;
    if (Capacitor.isNativePlatform()) {
      root.classList.add("cap-native");
    }
    if ($theme === "light") {
      root.setAttribute("data-theme", "light");
      root.style.setProperty("--date-picker-background", "#FFFFFF");
      root.style.setProperty("--date-picker-foreground", "#27272A");
    } else {
      root.setAttribute("data-theme", "dark");
      root.style.setProperty("--date-picker-background", "#27272A");
      root.style.setProperty("--date-picker-foreground", "#FFFFFF");
    }

    await setTheme();
    showOnlineIndicator = Boolean(await storage.get("showOnlineIndicator"));

    await checkAppVersion();
  });

  const socket = useSocket({ componentName: 'App' });
  const {isConnected} = socket;
  
  // Subscribe to changeTrainer events for clients
  $: if ($isConnected && $user && isClient($user)) {
    socket.emit("subscribe", { event: "changeTrainer", id: $user.id });
    socket.on("changeTrainer", () => {
      fetchUser();
      replace("/training/program");
    });
  }

  setTimeout(() => {
    showOnlyUnreadChat.subscribe((res) => {
      if (res !== -1) {
        chatSessions.set([]);
        chatSessionsCount.set(0);
        fetchChatSessions();
      }
    });
  }, 3000);

  const socketDisconnect = () => {
    if ($isConnected && $user) {
      socket.emit("unsubscribe", { event: "changeTrainer", id: $user.id });
    }
  };

  onDestroy(() => {
    unsubscribeLocation();
    socketDisconnect();
    clearInterval(interval);
  });

  const conditionsFailed = (): void => {
    replace("/auth");
  };
</script>

<div
  class={`relative flex-col center-center bg-white dark:bg-zinc-800 text-slate-900 dark:text-slate-100 ${Capacitor.isNativePlatform() ? "extra-height" : ""}`}
>
  <div class="content-container w-full min-h-screen max-w-screen-sm" id="contentContainer">
    <Router {routes} on:conditionsFailed={conditionsFailed} />
  </div>
  {#if $dialogData.type}
    <Overlay />
    <Dialog isScrollable={$dialogData.type === dialogTypes.COMPARE_PHOTOS ? false : true} />
  {/if}
  <Menu />
  {#if showOnlineIndicator}
    <OnlineIndicator />
  {/if}
  <JobSpinner />
  {#if $showAlert.message !== ""}
    <Alert />
  {/if}
  {#if $alertStore.length > 0}
    <div class="p-2 fixed bottom-12 left-0 w-full flex flex-col gap-2">
      {#each $alertStore as alert (alert.id)}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div
          class="p-2 bg-white text-center border border-slate-300 rounded shadow-lg text-{alert.type ===
          'primary'
            ? 'black'
            : 'red-400'}"
          animate:flip={{ duration: 333 }}
          on:click={() => alertStore.hide(alert.id)}
        >
          {alert.message}
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .extra-height {
    height: calc(100vh - 48px);
  }
</style>
